import { useState } from "react";
import { Link, NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { LayoutDashboard, Users, Building2, LogOut, GraduationCap, BarChart3, ListChecks, Menu, X, Database, History, Activity, Image, ShoppingBag, MessageSquare } from "lucide-react";

const NAV = [
  { to: "/admin/dashboard", label: "Dashboard", icon: LayoutDashboard },
  { to: "/admin/workqueue", label: "Work Queue", icon: ListChecks },
  { to: "/admin/leads", label: "Leads", icon: Users },
  { to: "/admin/orders", label: "Orders", icon: ShoppingBag },
  { to: "/admin/analytics", label: "Founder Analytics", icon: BarChart3 },
  { to: "/admin/colleges", label: "Colleges", icon: Building2 },
  { to: "/admin/campus-truth", label: "Campus Truth Reviews", icon: MessageSquare },
  { to: "/admin/mid-import", label: "MID Import", icon: Database },
  { to: "/admin/media-import", label: "Media Import", icon: Image },
  { to: "/admin/data-health", label: "Data Health", icon: Activity },
  { to: "/admin/import-history", label: "Import History", icon: History },
];

function NavItems({ onNavigate }) {
  return (
    <>
      {NAV.map((n) => (
        <NavLink
          key={n.to}
          to={n.to}
          onClick={onNavigate}
          data-testid={`admin-nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
          className={({ isActive }) =>
            `flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors ${
              isActive ? "bg-white/15 text-white" : "text-white/70 hover:bg-white/10 hover:text-white"
            }`
          }
        >
          <n.icon className="h-4 w-4" /> {n.label}
        </NavLink>
      ))}
    </>
  );
}

export default function AdminLayout() {
  const { admin, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const handleLogout = () => {
    logout();
    navigate("/admin/login");
  };
  return (
    <div className="min-h-screen flex bg-slate-50">
      {/* Desktop sidebar */}
      <aside className="hidden md:flex w-64 flex-col bg-[#0B2A6B] text-white">
        <Link to="/admin/dashboard" className="flex items-center gap-2 px-6 h-16 border-b border-white/10">
          <GraduationCap className="h-6 w-6 text-[#D4A24C]" />
          <span className="font-display font-extrabold">CollegeUnlock</span>
        </Link>
        <nav className="flex-1 px-3 py-6 space-y-1">
          <NavItems />
        </nav>
        <button
          data-testid="admin-logout-btn"
          onClick={handleLogout}
          className="flex items-center gap-3 px-6 py-4 text-sm text-white/70 hover:text-white border-t border-white/10"
        >
          <LogOut className="h-4 w-4" /> Logout
        </button>
      </aside>

      {/* Mobile slide-over drawer */}
      {open && (
        <div className="md:hidden fixed inset-0 z-50 flex" data-testid="admin-mobile-drawer">
          <div className="absolute inset-0 bg-slate-900/50" onClick={() => setOpen(false)} />
          <aside className="relative w-64 flex flex-col bg-[#0B2A6B] text-white">
            <div className="flex items-center justify-between px-6 h-16 border-b border-white/10">
              <span className="flex items-center gap-2"><GraduationCap className="h-6 w-6 text-[#D4A24C]" /><span className="font-display font-extrabold">CollegeUnlock</span></span>
              <button data-testid="admin-drawer-close" onClick={() => setOpen(false)} className="text-white/70 hover:text-white"><X className="h-5 w-5" /></button>
            </div>
            <nav className="flex-1 px-3 py-6 space-y-1">
              <NavItems onNavigate={() => setOpen(false)} />
            </nav>
            <button onClick={handleLogout} className="flex items-center gap-3 px-6 py-4 text-sm text-white/70 hover:text-white border-t border-white/10">
              <LogOut className="h-4 w-4" /> Logout
            </button>
          </aside>
        </div>
      )}

      <div className="flex-1 flex flex-col min-w-0">
        <header className="h-16 bg-white border-b border-slate-200 flex items-center justify-between px-4 sm:px-6">
          <div className="md:hidden flex items-center gap-2">
            <button data-testid="admin-mobile-menu-btn" onClick={() => setOpen(true)} className="text-slate-600 hover:text-slate-900">
              <Menu className="h-6 w-6" />
            </button>
            <GraduationCap className="h-5 w-5 text-[#0B2A6B]" />
            <span className="font-display font-bold text-[#0B2A6B]">Admin</span>
          </div>
          <div className="hidden md:block font-display font-bold text-slate-900">Admin Panel</div>
          <div className="flex items-center gap-3">
            <span className="text-sm text-slate-500 hidden sm:block">{admin?.email}</span>
            <button onClick={handleLogout} className="md:hidden text-slate-500" data-testid="admin-logout-mobile">
              <LogOut className="h-5 w-5" />
            </button>
          </div>
        </header>
        <main className="flex-1 p-4 sm:p-6 overflow-x-hidden">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
